import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Button from '../ui/Button';
import { FiPlay, FiPause, FiSave, FiRotateCcw, FiMaximize } from 'react-icons/fi';

/**
 * Futuristic Timer Display — massive naked digital clock with minimal controls.
 */
export default function TimerDisplay({
  mode, chronoTime, chronoRunning, timerRemaining, timerRunning,
  timerStartDuration, formatTime,
  onChronoToggle, onChronoSave, onChronoReset,
  onTimerStart, onTimerPause, onTimerSave, onTimerReset,
  timerHours, timerMinutes, timerSeconds,
  setTimerHours, setTimerMinutes, setTimerSeconds,
  onFocusMode,
}) {
  const isRunning = mode === 'chronometer' ? chronoRunning : timerRunning;
  const displayTime = mode === 'chronometer' ? chronoTime : timerRemaining;
  const [tick, setTick] = useState(false);

  // Tick animation for the colon separator when running
  useEffect(() => {
    if (!isRunning) return;
    const interval = setInterval(() => setTick(t => !t), 500);
    return () => clearInterval(interval);
  }, [isRunning]);

  // Progress percentage for timer mode
  const timerProgress = mode === 'timer' && timerStartDuration > 0
    ? ((timerStartDuration - timerRemaining) / timerStartDuration) * 100
    : 0;

  // Split time into segments
  const h = Math.floor(displayTime / 3600);
  const m = Math.floor((displayTime % 3600) / 60);
  const s = displayTime % 60;

  return (
    <div className={`timer-display-container ${isRunning ? 'timer-running' : ''}`}>
      {/* Status indicator */}
      <div className="timer-status">
        <span className={`timer-status-dot ${isRunning ? 'timer-status-dot--active' : ''}`} />
        <span className="timer-status-label">
          {isRunning ? 'ODAKLANMA AKTİF' : displayTime > 0 ? 'DURAKLATILDI' : 'HAZIR'}
        </span>
      </div>

      {/* Giant Digital Clock */}
      <div className="timer-giant-clock">
        <div className="timer-digit-group">
          <span className="timer-digit">{h.toString().padStart(2, '0')}</span>
          <span className="timer-digit-label">saat</span>
        </div>
        <span className={`timer-colon ${isRunning && tick ? 'timer-colon--dim' : ''}`}>:</span>
        <div className="timer-digit-group">
          <span className="timer-digit">{m.toString().padStart(2, '0')}</span>
          <span className="timer-digit-label">dakika</span>
        </div>
        <span className={`timer-colon ${isRunning && tick ? 'timer-colon--dim' : ''}`}>:</span>
        <div className="timer-digit-group">
          <span className="timer-digit">{s.toString().padStart(2, '0')}</span>
          <span className="timer-digit-label">saniye</span>
        </div>
      </div>

      {/* Progress bar (timer mode only) */}
      {mode === 'timer' && timerStartDuration > 0 && (
        <motion.div 
          className="timer-progress-bar"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <div className="timer-progress-track">
            <motion.div 
              className="timer-progress-fill"
              animate={{ width: `${timerProgress}%` }}
              transition={{ duration: 0.3, ease: 'linear' }}
            />
          </div>
        </motion.div>
      )}

      {/* Timer Input (only for timer mode, before start) */}
      {mode === 'timer' && !timerRunning && timerRemaining === 0 && (
        <motion.div
          className="timer-inputs"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="timer-input-group">
            <input type="number" className="input timer-input" min={0} max={23} value={timerHours} onChange={(e) => setTimerHours(Number(e.target.value))} />
            <span className="timer-input-label">saat</span>
          </div>
          <div className="timer-input-group">
            <input type="number" className="input timer-input" min={0} max={59} value={timerMinutes} onChange={(e) => setTimerMinutes(Number(e.target.value))} />
            <span className="timer-input-label">dk</span>
          </div>
          <div className="timer-input-group">
            <input type="number" className="input timer-input" min={0} max={59} value={timerSeconds} onChange={(e) => setTimerSeconds(Number(e.target.value))} />
            <span className="timer-input-label">sn</span>
          </div>
        </motion.div>
      )}

      {/* Controls */}
      <div className="timer-controls">
        {mode === 'chronometer' ? (
          <>
            <button
              className={`timer-ctrl-btn timer-ctrl-btn--primary ${isRunning ? 'timer-ctrl-btn--pause' : ''}`}
              onClick={onChronoToggle}
            >
              {chronoRunning ? <FiPause size={24} /> : <FiPlay size={24} />}
            </button>
            <AnimatePresence>
              {chronoTime > 0 && !chronoRunning && (
                <motion.div 
                  className="timer-secondary-controls"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                >
                  <button className="timer-ctrl-btn timer-ctrl-btn--save" onClick={onChronoSave}>
                    <FiSave size={20} />
                    <span>Kaydet</span>
                  </button>
                  <button className="timer-ctrl-btn timer-ctrl-btn--ghost" onClick={onChronoReset}>
                    <FiRotateCcw size={20} />
                    <span>Sıfırla</span>
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </>
        ) : (
          <>
            {timerRemaining === 0 && !timerRunning ? (
              <button className="timer-ctrl-btn timer-ctrl-btn--primary" onClick={onTimerStart}>
                <FiPlay size={24} />
              </button>
            ) : (
              <>
                <button
                  className={`timer-ctrl-btn timer-ctrl-btn--primary ${timerRunning ? 'timer-ctrl-btn--pause' : ''}`}
                  onClick={timerRunning ? onTimerPause : onTimerStart}
                >
                  {timerRunning ? <FiPause size={24} /> : <FiPlay size={24} />}
                </button>
                <div className="timer-secondary-controls">
                  <button className="timer-ctrl-btn timer-ctrl-btn--save" onClick={onTimerSave}>
                    <FiSave size={20} />
                    <span>Kaydet</span>
                  </button>
                  <button className="timer-ctrl-btn timer-ctrl-btn--ghost" onClick={onTimerReset}>
                    <FiRotateCcw size={20} />
                    <span>Sıfırla</span>
                  </button>
                </div>
              </>
            )}
          </>
        )}
      </div>

      {/* Focus mode shortcut */}
      {isRunning && (
        <motion.button
          className="timer-focus-shortcut"
          onClick={onFocusMode}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          <FiMaximize size={16} />
          <span>Odak Modu</span>
        </motion.button>
      )}
    </div>
  );
}
